/**
 * Hook moments / power-word takeovers.
 *
 * A HookMoment is a RARE (≈1–4 per video), high-impact standalone text event -
 * the single most important word or phrase - that is rendered big, has its OWN
 * time window (it can linger past when the word was spoken), and can HIDE the
 * running captions for its duration. This is a different concept from per-line
 * keyword emphasis (`TranscriptWord.emphasized`), which fires on ~every line, in
 * place, timed to the spoken word.
 *
 * A hook can be a single title or several explicit LINES (newline-separated
 * text), each with its own look (font / weight / italic / size / colour / fill /
 * strike) and reveal time, and animates with a full motion model (see
 * hook-motion.ts): entrance + exit phases, per line/word/letter cascades, a hold
 * motion, optional head-tracking, and textured "footage" fills.
 *
 * Like `drawCaption`, `drawHookMoment` is shared by the browser preview and the
 * export renderer so the two cannot drift.
 */
import { clamp01 } from './animation.js';
import { contrastingStroke } from './style.js';
import { cssFont } from './layout.js';
import { resolveHookMotion, unitMotionAt, idleMotionAt, } from './hook-motion.js';
import { parseHex, metalGradient, drawBlurred, ITALIC_SKEW } from './paint.js';
import { trackOffset } from './hook-track.js';
export * from './hook-motion.js';
export * from './hook-track.js';
/** Clearing every style field, so switching presets never leaves stray styling. */
const CLEAR = {
    bgColor: undefined,
    stroke: undefined,
    gradient: undefined,
    glow: undefined,
    fill: 'solid',
    color: '#ffffff',
    fontFamily: 'Inter',
    fontWeight: 900,
    italic: undefined,
    textCase: undefined,
    tracking: undefined,
    lineHeight: undefined,
    opacity: undefined,
    texture: undefined,
    lineStyles: undefined,
    align: undefined,
};
/** Default footage-fill look: a touch magnified + brightened so it reads as texture. */
export const DEFAULT_TEXTURE = { zoom: 1.6, blur: 0.15, brightness: 0.15, saturation: 1, tintAmount: 0 };
export const HOOK_STYLE_PRESETS = [
    { id: 'clean', name: 'Clean Title', desc: 'plain white title with a soft shadow — the modern default, fits anything', patch: { ...CLEAR } },
    { id: 'yellow-pill', name: 'Yellow Pill', desc: 'dark text on a bright yellow pill — loud and punchy', patch: { ...CLEAR, bgColor: '#ffd400' } },
    { id: 'dark-pill', name: 'Dark Pill', desc: 'white text on a solid dark pill — clean and legible on busy footage', patch: { ...CLEAR, bgColor: '#101216' } },
    { id: 'neon', name: 'Neon Glow', desc: 'bright text with a cyan glow — hype / music / gaming energy', patch: { ...CLEAR, fontFamily: 'Poppins', color: '#eafcff', glow: { color: '#22d3ee', blur: 0.22 }, animation: 'popShake' } },
    { id: 'sticker', name: 'Sticker', desc: 'bright rounded fill with a thick white sticker outline — fun / playful', patch: { ...CLEAR, fontFamily: 'Baloo2', fontWeight: 800, color: '#ff5d8f', stroke: { color: '#ffffff', width: 0.11 } } },
    { id: 'gradient-gold', name: 'Gradient Gold', desc: 'warm gold gradient title — premium / luxury / money', patch: { ...CLEAR, gradient: { from: '#fff3c4', to: '#f59e0b', angleDeg: 90 } } },
    { id: 'outline', name: 'Outline', desc: 'hollow outlined title (footage shows through) — bold but airy', patch: { ...CLEAR, color: 'transparent', stroke: { color: '#ffffff', width: 0.05 } } },
    { id: 'marker', name: 'Marker', desc: 'dark text on a lime marker bar — tactile, casual', patch: { ...CLEAR, fontFamily: 'Poppins', bgColor: '#b6f36c' } },
    { id: 'script', name: 'Script', desc: 'gold handwriting script — personal / lifestyle / vlog', patch: { ...CLEAR, fontFamily: 'Caveat', fontWeight: 700, color: '#ffd36e' } },
    { id: 'shine', name: 'Shine Sweep', desc: 'gold title with a gloss that keeps sweeping across — premium, animated', patch: { ...CLEAR, color: '#ffb03a', fill: 'shine' } },
    { id: 'flow', name: 'Gradient Flow', desc: 'a violet→cyan gradient that flows through the text — hype, animated', patch: { ...CLEAR, fontFamily: 'Poppins', gradient: { from: '#a78bfa', to: '#22d3ee', angleDeg: 0 }, fill: 'flow' } },
    { id: 'wipe', name: 'Fill Wipe', desc: 'colour wipes across the word like karaoke, then holds — animated', patch: { ...CLEAR, color: '#39ff14', fill: 'wipe' } },
    // ── Studio-only editorial looks (multi-line, textured) ──
    {
        id: 'editorial-stack', name: 'Editorial Stack', studioOnly: true,
        desc: 'thin italic + heavy italic lines stacked tight — one idea per line',
        patch: {
            ...CLEAR, textCase: 'asis', align: 'left', italic: true, lineHeight: 0.98, fontWeight: 800,
            lineStyles: [{ fontWeight: 300 }, null, null, null, { fontWeight: 300, size: 0.62 }],
        },
    },
    {
        id: 'kicker-big', name: 'Kicker + Big', studioOnly: true,
        desc: 'a small kicker line over one huge word',
        patch: { ...CLEAR, lineHeight: 0.96, lineStyles: [{ size: 0.36, fontWeight: 600 }, { size: 1.35 }] },
    },
    {
        id: 'serif-mix', name: 'Serif Mix', studioOnly: true,
        desc: 'an elegant serif with a coloured italic accent line',
        patch: {
            ...CLEAR, fontFamily: 'PlayfairDisplay', fontWeight: 500, textCase: 'asis', lineHeight: 1.05,
            lineStyles: [null, { fontFamily: 'PlayfairItalic', color: '#a855f7', size: 1.15 }],
        },
    },
    {
        id: 'script-accent', name: 'Script Accent', studioOnly: true,
        desc: 'a clean word with a brush-script line swooping across it',
        patch: {
            ...CLEAR, textCase: 'asis', fontWeight: 500, lineHeight: 0.8,
            lineStyles: [{ size: 1.15 }, { fontFamily: 'Yellowtail', color: '#ff4d2e', size: 1.05 }],
        },
    },
    {
        id: 'footage', name: 'Footage Fill', studioOnly: true,
        desc: 'huge letters filled with the video itself',
        patch: { ...CLEAR, fontFamily: 'Montserrat', fill: 'footage', texture: { ...DEFAULT_TEXTURE }, stroke: undefined },
    },
    {
        id: 'footage-tint', name: 'Tinted Footage', studioOnly: true,
        desc: 'footage-filled letters colourised yellow, with a thin kicker',
        patch: {
            ...CLEAR, fontFamily: 'Montserrat', fill: 'footage', lineHeight: 0.98,
            texture: { zoom: 1.2, blur: 0, brightness: 0.35, saturation: 0.4, tint: '#ffe600', tintAmount: 0.75 },
            lineStyles: [{ size: 0.4, fontWeight: 500, fill: 'solid', color: '#ffffff' }],
        },
    },
    {
        id: 'metal-gold', name: 'Gold Metal', studioOnly: true,
        desc: 'polished gold metal letters',
        patch: { ...CLEAR, fontFamily: 'Montserrat', color: '#d9a520', fill: 'metal' },
    },
];
/** Preset lookup by id (for kMagic + the picker). */
export const HOOK_STYLE_PRESETS_BY_ID = Object.fromEntries(HOOK_STYLE_PRESETS.map((p) => [p.id, p]));
/** The hook active at `timeMs` (last one wins if they overlap), or null. */
export function activeHookAt(hooks, timeMs) {
    if (!hooks || hooks.length === 0)
        return null;
    let found = null;
    for (const h of hooks) {
        if (timeMs >= h.startMs && timeMs < h.endMs)
            found = h;
    }
    return found;
}
/** True when a hook wants the normal captions ducked out at `timeMs`. */
export function captionsHiddenAt(hooks, timeMs) {
    const h = activeHookAt(hooks, timeMs);
    return Boolean(h && h.hideCaptions);
}
/** True when any part of the hook fills its letters with the footage. */
export function hookUsesFootage(h) {
    return h.fill === 'footage' || Boolean(h.lineStyles?.some((l) => l?.fill === 'footage'));
}
/** Pick a readable text color (dark/light) for text sitting on a filled `bg`. */
export function readableTextColor(bg) {
    const rgb = parseHex(bg);
    if (!rgb)
        return '#ffffff';
    const lum = (0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2]) / 255;
    return lum > 0.6 ? '#101216' : '#ffffff';
}
/** Whole-block animation state at `nowMs` (block split, no cascade). */
export function hookEnvelope(hook, nowMs, refHeight) {
    const motion = resolveHookMotion(hook);
    const st = unitMotionAt(motion, hook, hook.startMs, nowMs, {
        width: refHeight * 0.5625,
        height: refHeight,
        fontPx: refHeight * 0.1,
    });
    return { opacity: st.opacity, scale: st.scale, translateYPx: st.ty, rotateDeg: st.rotateDeg };
}
function anchorY(anchor, height) {
    switch (anchor) {
        case 'upper':
            // High in the frame so a talking-head subject only occludes the title's
            // lower edge (the modern behind-the-subject look), not the whole face.
            return height * 0.24;
        case 'lower':
            return height * 0.72;
        case 'center':
        default:
            return height * 0.5;
    }
}
/** Wrap `text` into lines that fit `maxWidth` for the given css font. */
function wrapText(text, font, maxWidth, measurer) {
    const words = text.split(/\s+/).filter(Boolean);
    if (words.length === 0)
        return [];
    const lines = [];
    let cur = words[0];
    for (let i = 1; i < words.length; i++) {
        const next = `${cur} ${words[i]}`;
        if (measurer.measure(next, font) <= maxWidth)
            cur = next;
        else {
            lines.push(cur);
            cur = words[i];
        }
    }
    lines.push(cur);
    return lines;
}
const SKEW = ITALIC_SKEW;
function applyCase(s, c) {
    return c === 'upper' ? s.toUpperCase() : c === 'lower' ? s.toLowerCase() : s;
}
/** Text width including letter spacing. */
function trackedWidth(text, font, trackPx, m) {
    const n = [...text].length;
    return m.measure(text, font) + Math.max(0, n - 1) * trackPx;
}
function resolveLineStyle(h, i, family) {
    const ls = h.lineStyles?.[i] ?? null;
    const fill = ls?.fill ?? (ls?.color ? 'solid' : h.fill ?? 'solid');
    return {
        family: ls?.fontFamily ?? family,
        weight: ls?.fontWeight ?? h.fontWeight ?? 900,
        italic: ls?.italic ?? h.italic ?? false,
        sizeMul: ls?.size ?? 1,
        color: ls?.color ?? h.color,
        fill,
        gradient: ls?.color && !ls?.fill ? undefined : h.gradient,
        strike: ls?.strike,
        delayMs: ls?.delayMs,
        textCase: ls?.textCase ?? h.textCase ?? 'upper',
    };
}
// ─── Drawing ─────────────────────────────────────────────────────────────────
/**
 * Draw one hook takeover frame onto `ctx`. Idempotent per frame; call after (or
 * instead of) drawCaption.
 */
export function drawHookMoment(ctx, d) {
    const { hook, nowMs, width, height, measurer } = d;
    if (nowMs < hook.startMs || nowMs >= hook.endMs)
        return;
    const c = ctx;
    const motion = resolveHookMotion(hook);
    const family = hook.fontFamily ?? d.fontFamily ?? 'Inter';
    const lh = hook.lineHeight ?? 1.06;
    const explicit = hook.text.includes('\n');
    // Base ~10% of frame height, scaled; opening hooks run a touch smaller. Kept
    // deliberately modest so a behind-the-subject title sits ABOVE the head and
    // only its bottom edge is occluded, rather than covering the whole face.
    const baseFrac = hook.type === 'opening' ? 0.085 : 0.1;
    const maxWidth = width * 0.84;
    const maxBlockH = height * (explicit ? 0.62 : 0.38);
    let basePx = height * baseFrac * (hook.scale || 1);
    const trackEm = hook.tracking ?? 0;
    // Raw lines + their styles.
    const rawTexts = explicit
        ? hook.text.split('\n').map((s) => s.trim()).filter(Boolean)
        : [hook.text];
    if (rawTexts.length === 0)
        return;
    const build = () => {
        if (!explicit) {
            // Auto-wrapped single title (the classic layout): one style for every line.
            const st = resolveLineStyle(hook, 0, family);
            const text = applyCase(hook.text, st.textCase);
            let fontPx = basePx * st.sizeMul;
            let font = cssFont(st.family, st.weight, Math.round(fontPx));
            let lines = wrapText(text, font, maxWidth, measurer);
            if (lines.length === 0)
                return [];
            // Auto-fit: keep a multi-word title a title, not a wall of text.
            if (fontPx * lh * lines.length > maxBlockH) {
                fontPx *= maxBlockH / (fontPx * lh * lines.length);
                font = cssFont(st.family, st.weight, Math.round(fontPx));
                lines = wrapText(text, font, maxWidth, measurer);
            }
            // Width auto-fit: a single long word can't wrap - shrink so it fits.
            const tp = () => trackEm * fontPx;
            const ww = lines.reduce((m, l) => Math.max(m, trackedWidth(l, font, tp(), measurer)), 0);
            if (ww > maxWidth) {
                fontPx *= maxWidth / ww;
                font = cssFont(st.family, st.weight, Math.round(fontPx));
                lines = wrapText(text, font, maxWidth, measurer);
            }
            return lines.map((ln, i) => {
                const ls = i === 0 ? st : { ...resolveLineStyle(hook, i, family), family: st.family, weight: st.weight, sizeMul: 1 };
                return mkLine(ln, font, fontPx, trackEm * fontPx, ls);
            });
        }
        // Explicit lines: each line its own style + size; shrink a line to fit width.
        return rawTexts.map((raw, i) => {
            const st = resolveLineStyle(hook, i, family);
            const text = applyCase(raw, st.textCase);
            let fontPx = basePx * st.sizeMul;
            let font = cssFont(st.family, st.weight, Math.round(fontPx));
            const w = trackedWidth(text, font, trackEm * fontPx, measurer);
            if (w > maxWidth) {
                fontPx *= maxWidth / w;
                font = cssFont(st.family, st.weight, Math.round(fontPx));
            }
            return mkLine(text, font, fontPx, trackEm * fontPx, st);
        });
    };
    const mkLine = (text, font, fontPx, trackPx, style) => ({
        text,
        font,
        fontPx,
        width: trackedWidth(text, font, trackPx, measurer),
        x0: 0,
        top: 0,
        baseline: 0,
        advance: fontPx * lh,
        trackPx,
        style,
        units: [],
    });
    let lines = build();
    if (lines.length === 0)
        return;
    // Explicit stacks: keep the whole block within the height budget.
    let blockH = lines.reduce((s, l) => s + l.advance, 0);
    if (explicit && blockH > maxBlockH) {
        basePx *= maxBlockH / blockH;
        lines = build();
        blockH = lines.reduce((s, l) => s + l.advance, 0);
    }
    const blockW = lines.reduce((m, l) => Math.max(m, l.width), 0);
    // Position: vertical anchor clamped to the safe zone; horizontal offset clamped
    // so the block stays on screen.
    const half = blockH / 2;
    const minCy = height * 0.1 + half;
    const maxCy = height * 0.86 - half;
    const wantCy = anchorY(hook.anchor, height) + (hook.offsetYNorm ?? 0) * height;
    const cy = minCy > maxCy ? height / 2 : Math.max(minCy, Math.min(maxCy, wantCy));
    const wantCx = width / 2 + (hook.offsetXNorm ?? 0) * width;
    const halfW = blockW / 2;
    const cx = halfW * 2 >= width * 0.92 ? width / 2 : Math.max(width * 0.04 + halfW, Math.min(width * 0.96 - halfW, wantCx));
    const align = hook.align ?? 'center';
    let y = cy - blockH / 2;
    for (const ln of lines) {
        ln.top = y;
        // Descender room stays ~0.06em whatever the line height (legacy layout).
        ln.baseline = y + ln.fontPx * (lh - 0.06);
        ln.x0 = align === 'left' ? cx - blockW / 2 : align === 'right' ? cx + blockW / 2 - ln.width : cx - ln.width / 2;
        y += ln.advance;
    }
    // ── Units + their motion ──
    const split = motion.split;
    const hasDelays = explicit && Boolean(hook.lineStyles?.some((l) => l?.delayMs != null));
    const eff = split === 'block' && hasDelays ? 'line' : split;
    const stagger = Math.max(0, motion.staggerMs);
    let order = 0;
    lines.forEach((ln, li) => {
        const lineDelay = hasDelays ? (ln.style.delayMs ?? li * stagger) : 0;
        const lineCy = ln.baseline - ln.fontPx * 0.36;
        const push = (text, start, idxInLine) => {
            const xOff = measurer.measure(ln.text.slice(0, start), ln.font) + [...ln.text.slice(0, start)].length * ln.trackPx;
            const w = trackedWidth(text, ln.font, ln.trackPx, measurer);
            const reveal = hookRevealMs(hook.startMs, eff, hasDelays, lineDelay, idxInLine, order, li, stagger);
            const u = {
                text,
                start,
                xOff,
                w,
                revealMs: reveal,
                ox: eff === 'block' ? cx : eff === 'line' ? ln.x0 + ln.width / 2 : ln.x0 + xOff + w / 2,
                oy: eff === 'block' ? cy : lineCy,
                st: unitMotionAt(motion, hook, reveal, nowMs, { width, height, fontPx: ln.fontPx }),
            };
            ln.units.push(u);
            order++;
        };
        if (eff === 'block' || eff === 'line')
            push(ln.text, 0, 0);
        else if (eff === 'word') {
            let k = 0;
            for (const m of ln.text.matchAll(/\S+/g))
                push(m[0], m.index ?? 0, k++);
        }
        else {
            let k = 0;
            const chars = [...ln.text];
            let pos = 0;
            for (const ch of chars) {
                if (ch.trim())
                    push(ch, pos, k++);
                pos += ch.length;
            }
        }
    });
    const visible = lines.some((l) => l.units.some((u) => u.st.opacity > 0.005));
    if (!visible)
        return;
    // ── Block transform: tilt, hold motion, head tracking ──
    const idle = idleMotionAt(motion, hook, nowMs, { width, height });
    const trk = trackOffset(d.track, hook.track, hook, nowMs, { x: cx, y: cy }, { width, height, fit: d.trackFit });
    const tilt = (hook.rotateDeg ?? 0) + idle.rotateDeg;
    const blockScale = idle.scale * trk.scale;
    const applyBlock = (k) => {
        k.translate(cx + idle.tx + trk.dx, cy + idle.ty + trk.dy);
        if (tilt)
            k.rotate((tilt * Math.PI) / 180);
        if (blockScale !== 1)
            k.scale(blockScale, blockScale);
        k.translate(-cx, -cy);
    };
    const baseAlpha = clamp01(hook.opacity ?? 1);
    /** Run `fn` with the unit's transform (+ mask clip + italic skew) applied. */
    const withUnit = (k, ln, u, fn, alpha = true) => {
        if (u.st.opacity <= 0.005)
            return;
        k.save();
        let mty = 0;
        if (u.st.masked) {
            // Mask box = the line's glyph band; the unit slides in/out of it.
            const top = ln.baseline - ln.fontPx * 0.92;
            const boxH = ln.fontPx * 1.16;
            k.beginPath();
            k.rect(-width, top, width * 3, boxH);
            k.clip();
            mty = u.st.maskOffset * boxH;
        }
        k.translate(u.ox + u.st.tx, u.oy + u.st.ty + mty);
        if (u.st.rotateDeg)
            k.rotate((u.st.rotateDeg * Math.PI) / 180);
        if (u.st.scale !== 1)
            k.scale(u.st.scale, u.st.scale);
        k.translate(-u.ox, -u.oy);
        if (ln.style.italic && k.transform) {
            const px = ln.x0 + u.xOff;
            k.translate(px, ln.baseline);
            k.transform(1, 0, SKEW, 1, 0, 0);
            k.translate(-px, -ln.baseline);
        }
        if (alpha)
            k.globalAlpha = baseAlpha * clamp01(u.st.opacity);
        fn();
        k.restore();
    };
    /** Draw a unit's glyphs (fill or stroke), honouring letter spacing. */
    const glyphs = (k, ln, u, op, dx = 0, dy = 0) => {
        const extra = u.st.trackEm * ln.fontPx;
        const x = ln.x0 + u.xOff + dx;
        const by = ln.baseline + dy;
        if (ln.trackPx === 0 && extra === 0) {
            if (op === 'fill')
                k.fillText(u.text, x, by);
            else
                k.strokeText(u.text, x, by);
            return;
        }
        const chars = [...u.text];
        const n = chars.length;
        let acc = '';
        chars.forEach((ch, i) => {
            const cxp = x + measurer.measure(acc, ln.font) + i * ln.trackPx + (i - (n - 1) / 2) * extra;
            acc += ch;
            if (!ch.trim())
                return;
            if (op === 'fill')
                k.fillText(ch, cxp, by);
            else
                k.strokeText(ch, cxp, by);
        });
    };
    const eachUnit = (fn) => lines.forEach((ln) => ln.units.forEach((u) => fn(ln, u)));
    const BLUR_MIN = 0.6;
    const blurred = (u) => u.st.blurPx > BLUR_MIN;
    // The caller's matrix (e.g. the renderer's lower-half offset), for compositing
    // frame-space surfaces back in.
    const entry = c.getTransform?.() ?? null;
    c.save();
    c.textAlign = 'left';
    c.textBaseline = 'alphabetic';
    applyBlock(c);
    // Critique overlay: just a thin bright outline so the model can see a
    // behind-hook's full extent through the person that occludes it.
    if (d.outlineOnly) {
        c.lineJoin = 'round';
        c.strokeStyle = 'rgba(120,225,255,0.95)';
        eachUnit((ln, u) => withUnit(c, ln, u, () => {
            c.font = ln.font;
            c.lineWidth = Math.max(1.5, ln.fontPx * 0.045);
            glyphs(c, ln, u, 'stroke');
        }));
        c.restore();
        return;
    }
    // Block bounds (for block-wide gradients / animated fills).
    const bb = { left: cx - blockW / 2, right: cx + blockW / 2, cx, cy, blockH, fontPx: lines[0].fontPx, top: 0, bottom: 0 };
    // 1. Pills behind each line (only when a look opts into a pill).
    if (hook.bgColor) {
        c.fillStyle = hook.bgColor;
        for (const ln of lines) {
            const u0 = ln.units[0];
            if (!u0)
                continue;
            const lineU = { ...u0, ox: eff === 'block' ? cx : ln.x0 + ln.width / 2, xOff: 0 };
            withUnit(c, ln, lineU, () => {
                const padX = ln.fontPx * 0.32;
                const padY = ln.fontPx * 0.16;
                const boxTop = ln.top + ln.advance * 0.16 - padY;
                roundRect(c, ln.x0 - padX, boxTop, ln.width + padX * 2, ln.fontPx + padY * 2, ln.fontPx * 0.22);
                c.fill();
            });
        }
    }
    const solidOf = (ln) => {
        if (hook.bgColor && !hook.lineStyles?.[lines.indexOf(ln)]?.color)
            return readableTextColor(hook.bgColor);
        if (ln.style.fill === 'footage')
            return hook.texture?.tint && (hook.texture.tintAmount ?? 0) > 0.3 ? hook.texture.tint : '#ffffff';
        if (ln.style.gradient)
            return ln.style.gradient.from;
        return ln.style.color && ln.style.color !== 'transparent' ? ln.style.color : '#ffffff';
    };
    // 2. Shadow / glow pass (under the fill).
    if (!hook.bgColor) {
        eachUnit((ln, u) => {
            if (blurred(u))
                return;
            withUnit(c, ln, u, () => {
                c.font = ln.font;
                if (hook.glow) {
                    c.shadowColor = hook.glow.color;
                    c.shadowBlur = Math.max(1, hook.glow.blur * ln.fontPx);
                    c.fillStyle = lineFill(c, hook, ln, nowMs, bb);
                    glyphs(c, ln, u, 'fill');
                    glyphs(c, ln, u, 'fill');
                }
                else {
                    // Clean title: a soft floating drop-shadow for depth + legibility.
                    c.shadowColor = 'rgba(0,0,0,0.55)';
                    c.shadowBlur = ln.fontPx * 0.3;
                    c.shadowOffsetY = ln.fontPx * 0.05;
                    c.fillStyle = ln.style.fill === 'footage' ? '#000000' : lineFill(c, hook, ln, nowMs, bb);
                    glyphs(c, ln, u, 'fill');
                }
            });
        });
    }
    // 3. Stroke pass: the hook's own outline, else a default hairline edge
    //    (skipped when a glow already separates the text from the footage).
    if (!hook.bgColor) {
        eachUnit((ln, u) => {
            if (blurred(u))
                return;
            withUnit(c, ln, u, () => {
                c.font = ln.font;
                c.lineJoin = 'round';
                if (hook.stroke && hook.stroke.width > 0) {
                    c.lineWidth = Math.max(1, hook.stroke.width * ln.fontPx);
                    const fill = lineFill(c, hook, ln, nowMs, bb);
                    // Guard against a white fill inside a white outline (blends away).
                    c.strokeStyle = typeof fill === 'string' ? contrastingStroke(hook.stroke.color, fill) : hook.stroke.color;
                    glyphs(c, ln, u, 'stroke');
                }
                else if (!hook.glow) {
                    c.lineWidth = Math.max(1, ln.fontPx * 0.04);
                    c.strokeStyle = 'rgba(0,0,0,0.30)';
                    glyphs(c, ln, u, 'stroke');
                }
            });
        });
    }
    // 4. Fill pass. Blurred (mid blur-in/out) units draw a soft blurred copy;
    //    footage-filled units are collected and composited from the video.
    const footageUnits = [];
    const canFootage = Boolean(d.footage && d.scratch && c.getTransform && c.drawImage);
    eachUnit((ln, u) => {
        if (blurred(u)) {
            withUnit(c, ln, u, () => {
                c.font = ln.font;
                drawBlurred(c, (dx, dy) => glyphs(c, ln, u, 'fill', dx, dy), solidOf(ln), u.st.blurPx, width);
            });
            return;
        }
        if (ln.style.fill === 'footage' && !hook.bgColor && canFootage) {
            footageUnits.push([ln, u]);
            return;
        }
        withUnit(c, ln, u, () => {
            c.font = ln.font;
            c.fillStyle = (hook.bgColor ? solidOf(ln) : lineFill(c, hook, ln, nowMs, bb));
            glyphs(c, ln, u, 'fill');
        });
    });
    if (footageUnits.length) {
        const ok = compositeFootage(c, d, footageUnits, applyBlock, withUnit, glyphs, cx, cy, entry);
        if (!ok) {
            for (const [ln, u] of footageUnits) {
                withUnit(c, ln, u, () => {
                    c.font = ln.font;
                    c.fillStyle = solidOf(ln);
                    glyphs(c, ln, u, 'fill');
                });
            }
        }
    }
    // 5. Strike-throughs: drawn across the line once it has landed.
    for (const ln of lines) {
        if (!ln.style.strike || ln.units.length === 0)
            continue;
        const last = ln.units[ln.units.length - 1];
        const landed = last.revealMs + motion.in.durationMs;
        const p = clamp01((nowMs - landed) / 380);
        if (p <= 0)
            continue;
        const e = 1 - Math.pow(1 - p, 3);
        const alpha = Math.min(...ln.units.map((u) => u.st.opacity));
        if (alpha <= 0.01)
            continue;
        c.save();
        c.globalAlpha = baseAlpha * alpha;
        c.fillStyle = ln.style.strike;
        const th = Math.max(2, ln.fontPx * 0.075);
        const sy = ln.baseline - ln.fontPx * 0.32 - th / 2;
        const over = ln.fontPx * 0.08;
        c.beginPath();
        c.rect(ln.x0 - over, sy, (ln.width + over * 2) * e, th);
        c.fill();
        c.restore();
    }
    c.restore();
}
/** Absolute reveal time of a unit. */
function hookRevealMs(startMs, split, hasDelays, lineDelay, idxInLine, order, lineIdx, stagger) {
    if (hasDelays)
        return startMs + lineDelay + idxInLine * stagger;
    if (split === 'block')
        return startMs;
    if (split === 'line')
        return startMs + lineIdx * stagger;
    return startMs + order * stagger;
}
/**
 * Fill the given units' letters with the (zoomed / blurred / tinted) video
 * frame. Builds the footage + a glyph mask on scratch surfaces, then draws the
 * result into `c` at the hook's base transform. Returns false if unavailable.
 */
function compositeFootage(c, d, units, applyBlock, withUnit, glyphs, cx, cy, entry) {
    const { width: W, height: H, footage, hook } = d;
    if (!footage || !d.scratch)
        return false;
    const L = d.scratch('hook-footage', W, H);
    const Mk = d.scratch('hook-mask', W, H);
    if (!L || !Mk || !L.ctx.drawImage || !L.ctx.setTransform || !Mk.ctx.setTransform)
        return false;
    const tex = { ...DEFAULT_TEXTURE, ...(hook.texture ?? {}) };
    const fw = footage.width;
    const fh = footage.height;
    if (!(fw > 0 && fh > 0))
        return false;
    // 1. Footage, cover-fitted and magnified around the hook's center.
    const k = L.ctx;
    k.setTransform(1, 0, 0, 1, 0, 0);
    k.globalCompositeOperation = 'source-over';
    k.globalAlpha = 1;
    k.clearRect?.(0, 0, W, H);
    const sc = Math.max(W / fw, H / fh);
    const zoom = Math.max(1, tex.zoom);
    const bx = (W - fw * sc) / 2;
    const by = (H - fh * sc) / 2;
    const dx = cx + (bx - cx) * zoom;
    const dy = cy + (by - cy) * zoom;
    const dw = fw * sc * zoom;
    const dh = fh * sc * zoom;
    const blur = clamp01(tex.blur);
    if (blur > 0.01) {
        // Soften by drawing through a downscaled surface (smooth upscale).
        const f = 1 / (1 + blur * 14);
        const sw = Math.max(2, Math.round(W * f));
        const sh = Math.max(2, Math.round(H * f));
        const S = d.scratch('hook-footage-small', sw, sh);
        if (S?.ctx.drawImage && S.ctx.setTransform) {
            S.ctx.setTransform(1, 0, 0, 1, 0, 0);
            S.ctx.clearRect?.(0, 0, sw, sh);
            S.ctx.imageSmoothingEnabled = true;
            S.ctx.drawImage(footage.image, 0, 0, fw, fh, dx * f, dy * f, dw * f, dh * f);
            k.imageSmoothingEnabled = true;
            k.drawImage(S.canvas, 0, 0, sw, sh, 0, 0, W, H);
        }
        else {
            k.drawImage(footage.image, 0, 0, fw, fh, dx, dy, dw, dh);
        }
    }
    else {
        k.drawImage(footage.image, 0, 0, fw, fh, dx, dy, dw, dh);
    }
    // 2. Colour adjustments (whole surface; masked next).
    const fillAll = (op, style) => {
        k.globalCompositeOperation = op;
        k.fillStyle = style;
        k.beginPath();
        k.rect(0, 0, W, H);
        k.fill();
    };
    const sat = clamp01(tex.saturation);
    if (sat < 0.99)
        fillAll('saturation', `rgba(128,128,128,${(1 - sat).toFixed(3)})`);
    if (tex.tint && tex.tintAmount > 0.01) {
        const rgb = parseHex(tex.tint);
        if (rgb)
            fillAll('color', `rgba(${rgb[0]},${rgb[1]},${rgb[2]},${clamp01(tex.tintAmount).toFixed(3)})`);
    }
    const b = Math.max(-1, Math.min(1, tex.brightness));
    if (b > 0.01)
        fillAll('screen', `rgba(255,255,255,${(b * 0.7).toFixed(3)})`);
    else if (b < -0.01)
        fillAll('source-atop', `rgba(0,0,0,${(-b * 0.7).toFixed(3)})`);
    // 3. Glyph mask (union of every footage unit, with their motion + opacity).
    const m = Mk.ctx;
    m.setTransform(1, 0, 0, 1, 0, 0);
    m.globalCompositeOperation = 'source-over';
    m.globalAlpha = 1;
    m.clearRect?.(0, 0, W, H);
    m.save();
    m.textAlign = 'left';
    m.textBaseline = 'alphabetic';
    m.fillStyle = '#000000';
    applyBlock(m);
    for (const [ln, u] of units) {
        withUnit(m, ln, u, () => {
            m.font = ln.font;
            m.fillStyle = '#000000';
            glyphs(m, ln, u, 'fill');
        });
    }
    m.restore();
    // 4. Keep the footage only inside the letters.
    k.globalCompositeOperation = 'destination-in';
    k.globalAlpha = 1;
    k.drawImage(Mk.canvas, 0, 0);
    k.globalCompositeOperation = 'source-over';
    // 5. Composite into the frame at the caller's matrix (before the hook's own
    //    block transform) - the surfaces are already in frame space.
    c.save();
    if (entry && c.setTransform)
        c.setTransform(entry.a, entry.b, entry.c, entry.d, entry.e, entry.f);
    c.globalAlpha = 1;
    c.shadowColor = 'rgba(0,0,0,0)';
    c.shadowBlur = 0;
    c.drawImage(L.canvas, 0, 0);
    c.restore();
    return true;
}
const SHINE_MS = 2200;
const FLOW_MS = 3000;
const WIPE_MS = 2400;
/** The fillStyle for a line: solid, static gradient, metal, or an animated
 *  shine/flow/wipe advanced deterministically by `nowMs`. */
function lineFill(ctx, hook, ln, nowMs, bb) {
    const kind = ln.style.fill;
    const w = Math.max(1, bb.right - bb.left);
    const color = ln.style.color;
    if (kind === 'metal') {
        return metalGradient(ctx, color, ln.baseline - ln.fontPx * 0.78, ln.baseline + ln.fontPx * 0.04);
    }
    if (kind === 'shine') {
        const base = color || '#ffffff';
        const p = (nowMs % SHINE_MS) / SHINE_MS; // gloss sweep 0..1
        const g = ctx.createLinearGradient(bb.left, bb.cy - bb.fontPx * 0.4, bb.right, bb.cy + bb.fontPx * 0.4);
        const cc = clamp01(p);
        const lo = Math.max(0, cc - 0.16);
        const hi = Math.min(1, cc + 0.16);
        g.addColorStop(0, base);
        if (lo > 0)
            g.addColorStop(lo, base);
        g.addColorStop(cc, '#ffffff');
        if (hi < 1)
            g.addColorStop(hi, base);
        g.addColorStop(1, base);
        return g;
    }
    if (kind === 'wipe') {
        const on = color || '#39ff14';
        const off = 'rgba(255,255,255,0.20)';
        const p = clamp01((nowMs % WIPE_MS) / WIPE_MS / 0.6); // fill in the first 60%, then hold
        const g = ctx.createLinearGradient(bb.left, bb.cy, bb.right, bb.cy);
        const b = Math.min(0.9999, Math.max(0.0001, p));
        g.addColorStop(0, on);
        g.addColorStop(b, on);
        g.addColorStop(b, off);
        g.addColorStop(1, off);
        return g;
    }
    if (kind === 'flow') {
        const a = ln.style.gradient?.from ?? hook.gradient?.from ?? '#a78bfa';
        const bcol = ln.style.gradient?.to ?? hook.gradient?.to ?? '#22d3ee';
        // Scroll a 2-block-wide A,B,A,B,A gradient by one block per cycle (seamless).
        const shift = ((nowMs % FLOW_MS) / FLOW_MS) * w;
        const g = ctx.createLinearGradient(bb.left - w + shift, bb.cy, bb.right + shift, bb.cy);
        g.addColorStop(0, a);
        g.addColorStop(0.25, bcol);
        g.addColorStop(0.5, a);
        g.addColorStop(0.75, bcol);
        g.addColorStop(1, a);
        return g;
    }
    // solid, or a static gradient across the block along its angle.
    if (ln.style.gradient && kind !== 'footage') {
        const gr = ln.style.gradient;
        const rad = (((gr.angleDeg % 360) + 360) % 360) * (Math.PI / 180);
        const dx = Math.cos(rad);
        const dy = Math.sin(rad);
        const hw = w / 2;
        const hh = bb.blockH / 2;
        const g = ctx.createLinearGradient(bb.cx - dx * hw, bb.cy - dy * hh, bb.cx + dx * hw, bb.cy + dy * hh);
        g.addColorStop(0, gr.from);
        g.addColorStop(1, gr.to);
        return g;
    }
    return color;
}
function roundRect(ctx, x, y, w, h, r) {
    const radius = Math.min(r, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + w - radius, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
    ctx.lineTo(x + w, y + h - radius);
    ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
    ctx.lineTo(x + radius, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
}
/**
 * Normalize a hook list: sort by start time and keep at most one opening hook
 * (extras are demoted to 'power'). There is intentionally NO cap on the total
 * number of hooks - the user decides how many to use.
 */
export function normalizeHooks(hooks) {
    const sorted = [...hooks].sort((a, b) => a.startMs - b.startMs);
    let openingUsed = false;
    return sorted.map((h) => {
        if (h.type !== 'opening')
            return h;
        if (openingUsed)
            return { ...h, type: 'power' };
        openingUsed = true;
        return h;
    });
}
//# sourceMappingURL=hooks.js.map